import { Story } from '../../types/story';

export const lisbonRooftop: Story = {
  id: 'lisbon-rooftop',
  title: 'The Rooftop',
  excerpt: 'A sunset gathering in Lisbon leads to an unexpected connection under the stars.',
  city: 'Lisbon',
  publishDate: '2025-12-23',
  readTime: '13 min read',
  tags: ['♀♂', 'romantic', 'rooftop', 'sunset'],
  categories: ['romantic'],
  intensity: 1,
  content: `The rooftop bar was packed with tourists and locals alike, all gathered to watch the sunset over the Tagus River. I'd been in Lisbon for three days, falling more in love with the city with each passing hour—the pastel buildings, the melancholy fado music drifting from open windows, the way the light turned everything golden.

I'd claimed a spot at the edge of the terrace, leaning against the railing with a glass of vinho verde, when someone bumped into me from behind.

"Desculpe—sorry!" A woman's voice, Portuguese accent wrapping around the English words. She steadied herself, laughing. "These cobblestones and heels don't mix."

I turned to find her smiling apologetically, dark hair tumbling over bare shoulders, eyes the color of honey in the fading light.

"No harm done," I said, returning her smile. "The view's worth the crowd."

"Which view?" she asked, and there was something playful in her tone. "The sunset or the people watching?"

"Can't it be both?"

She laughed, a warm, genuine sound. "I like that answer. I'm Sofia."

"Daniel."

We stood together watching as the sun sank lower, painting the sky in shades of orange and pink. Sofia pointed out landmarks—the São Jorge Castle on the hill, the red bridge in the distance that everyone said looked like San Francisco's Golden Gate.

"You're not from Lisbon?" I asked.

"Porto originally. But I've been here five years now. Can't imagine leaving." She glanced at me. "And you? Tourist or temporary resident?"

"Tourist. Though I'm starting to understand why people never want to leave."

"It gets under your skin," she agreed. "The light, the music, the way time moves differently here. Slower. More intentional."

As the sun finally disappeared below the horizon, the crowd began to thin. Sofia didn't leave, and neither did I. We ordered more wine, found a quieter corner, and talked. About everything and nothing. She was an architect, working on a restoration project in Alfama. I told her about my work, my travels, the way I'd been feeling lately like I was searching for something without knowing what.

"Maybe you're not supposed to know," she said thoughtfully. "Maybe the searching is the point."

Hours passed. The bar began to close, staff politely suggesting we finish our drinks. Sofia looked at me, a question in her eyes.

"I live nearby," she said. "Just a few blocks. Would you like to see a real Lisbon apartment? Not the tourist version."

Her apartment was on the fourth floor of a building with no elevator, the walls covered in azulejo tiles. Inside, it was small but beautiful—high ceilings, tall windows, plants everywhere. She poured us port wine and we sat on her tiny balcony, the city spread out below us.

"This is my favorite time," she said softly. "When the tourists have gone to bed and Lisbon belongs to us again."

I don't remember who moved first. Maybe it was both of us at once. But suddenly we were kissing, her hands in my hair, mine on her waist. She tasted like port wine and possibility.

We moved inside, slowly, taking our time. She unbuttoned my shirt while I unzipped her dress. Every touch felt deliberate, meaningful. This wasn't just physical—it was connection, understanding, two people finding each other in a city built on hills and history.

In her bed, with the windows open and the sound of fado drifting up from somewhere below, we explored each other with a kind of reverent curiosity. She showed me what she liked, and I showed her the same. There was laughter and sighs and moments of perfect silence.

Afterward, we lay tangled together, her head on my chest, my fingers tracing patterns on her shoulder.

"Stay," she whispered. "Not just tonight. Stay in Lisbon. See what happens."

I wanted to. God, I wanted to. But I had a flight in two days, a job waiting, a life that existed somewhere else.

"I can't," I said, and felt her nod against my chest.

"I know. But I had to ask."

We made love again as dawn broke, slower this time, savoring every moment. And when I finally left, walking through the quiet morning streets back to my hotel, I carried her with me—the taste of her lips, the sound of her laugh, the way she'd looked at me like I was someone worth staying for.

I never saw Sofia again. But sometimes, when I hear fado music or see a sunset that's particularly beautiful, I think about that rooftop in Lisbon and the woman who showed me that some connections don't need to last forever to be perfect.`
};
